import React, { useState, useEffect } from 'react';
import './CinemaNews.css';

const NEWS_API = process.env.REACT_APP_API_URL || 'https://api.cinebotz.com';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const now = new Date();
  const sec = Math.floor((now - date) / 1000);
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return date.toLocaleDateString();
}

function normalizeItem(raw) {
  return {
    title: raw.title || raw.headline || '',
    link: raw.link || raw.url || raw.sourceUrl || '#',
    source: raw.source || raw.publisher || raw.site || 'News',
    date: raw.pubDate || raw.date || raw.publishedAt || raw.published || '',
    description: raw.description || raw.snippet || raw.summary || '',
    image: raw.image || raw.thumbnail || raw.imageUrl || '',
  };
}

export function CinemaNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = NEWS_API ? `${NEWS_API}/api/newsv2` : '/api/newsv2';
    fetch(url)
      .then((res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          return res.text().then(() => {
            throw new Error(
              res.ok
                ? 'Server did not return JSON.'
                : `News API error (${res.status}). Make sure the backend is running.`
            );
          });
        }
        if (!res.ok) {
          throw new Error(`News API error: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data.items || data.articles || data.news || []);
        setItems(list.slice(0, 5).map(normalizeItem));
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Could not load news.');
          setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="cinema-news cinema-news--loading">
        <div className="cinema-news-spinner" aria-hidden />
        <p className="cinema-news-loading-text">Loading News Botz…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cinema-news cinema-news--error">
        <p className="cinema-news-error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="cinema-news">
      <h1 className="cinema-news-title">News Botz</h1>
      <p className="cinema-news-subtitle">Latest headlines</p>
      <ul className="cinema-news-list">
        {items.map((item, i) => (
          <li key={i} className="cinema-news-card">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="cinema-news-card-link"
            >
              {item.image && (
                <div className="cinema-news-card-media">
                  <img src={item.image} alt="" className="cinema-news-card-img" />
                </div>
              )}
              <div className="cinema-news-card-body">
                <h2 className="cinema-news-card-title">{item.title}</h2>
                {item.description && (
                  <p className="cinema-news-card-desc">{item.description}</p>
                )}
                <div className="cinema-news-card-meta">
                  <span className="cinema-news-card-source">{item.source}</span>
                  <span className="cinema-news-card-time">
                    {formatTimeAgo(item.date)}
                  </span>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default CinemaNews;
